/**
 * web/src/shell/gate.tsx — the signed-out boot gate: landing beats, the Pair
 * screen and the legal footer, moved verbatim out of App.tsx (card
 * WUHnArc6lNID). Rendered in place of the chat panels while the session is
 * not 'chat'.
 */
import type { Dispatch, SetStateAction } from 'react';
import { Landing } from '../screens/Landing';
import { Pair } from '../screens/Pair';
import { LegalFooter } from '../components/LegalFooter';
import type { useSession } from '../hooks/useSession';
import type { LastIdentity } from '../lib/api';

interface BootGateProps {
  onChat: boolean;
  session: ReturnType<typeof useSession>;
  authOpen: boolean;
  setAuthOpen: Dispatch<SetStateAction<boolean>>;
  lastIdentity: LastIdentity | null;
  forgetIdentity: () => void;
}

export function BootGate({ onChat, session, authOpen, setAuthOpen, lastIdentity, forgetIdentity }: BootGateProps) {
  return (
    <>
      {/* Landing beats introduce the app to someone with nowhere else to be
          (card n3ZrSmsAlOpS). A partner arriving on an invite has somewhere
          specific to be — four screens of brand copy before the join box was
          the diversion the UX review named (item 3, H3), and an emailed
          invitee has already read enough to click. They get the join form on
          the first viewport instead. */}
      {!onChat && !session.pendingCode && !session.invitedLayout && !authOpen && <Landing />}
      {!onChat && (
        <Pair
          pendingCode={session.pendingCode}
          busy={session.busy}
          pairError={session.pairError}
          onRefreshInvite={session.refreshInvite}
          onClearPairError={session.clearPairError}
          authEmail={session.auth?.email ?? null}
          onCreate={name => void session.create(name)}
          onJoin={(code, name) => void session.join(code, name)}
          onJoinViaLink={(link, name) => void session.joinViaLink(link, name)}
          invitedLink={session.invitedLink}
          onSetInvitedLink={session.setInvitedLink}
          linkInfo={session.linkInfo}
          onRefreshLinkInfo={link => void session.refreshLinkInfo(link)}
          onAuthOpenChange={setAuthOpen}
          onSignIn={(email, password) => session.signIn(email, password)}
          onRegister={(email, password, name) => session.register(email, password, name)}
          oauthEnabled={session.authConfig?.oauth ?? false}
          oauthNotice={session.oauthNotice}
          verifyEnabled={session.authConfig?.verifyEmail ?? false}
          signupsOpen={session.authConfig?.signups === true}
          forgotEnabled={session.authConfig?.forgotPassword ?? false}
          mailNotice={session.mailNotice}
          invitedCode={session.invitedCode}
          invitedLayout={session.invitedLayout}
          onSetInvitedCode={session.setInvitedCode}
          resetToken={session.resetToken}
          emailVerified={session.emailVerified}
          onForgotPassword={session.forgotPassword}
          onApplyReset={session.applyResetPassword}
          onResendVerify={email => session.resendVerify(email)}
          onClearMailNotice={session.clearMailNotice}
          lastIdentity={lastIdentity}
          onForgetIdentity={forgetIdentity}
        />
      )}

      {/* Privacy / Terms footer on the signed-out page (card cEchedAassmt)
          — sits under the pair form so the landing scroll ends on it. */}
      {!onChat && <LegalFooter variant="page" />}
    </>
  );
}
